// Central content for the Gary Malkin Enterprises homepage.
// Institutional positioning: GME = the institution, Gary = founder/visionary,
// The Art of Human Being = philosophy, Resonance Intelligence = one methodology.

import { Feather, AudioWaveform, Flame, HeartPulse, MicVocal, Aperture } from "lucide-react";

export const IMG = {
  // retained
  orchestra: "https://images.unsplash.com/photo-1759502418465-62fb7c781e73?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  pianoSilhouette: "https://images.unsplash.com/photo-1773609628490-1108da35d4ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  keynote: "https://images.unsplash.com/photo-1626125345510-4603468eedfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  studio: "https://images.unsplash.com/photo-1771929182473-6daa8bf62907?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  leadership: "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  candle: "https://images.unsplash.com/photo-1601922046210-41e129a3e64a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  mountainSunrise: "https://images.unsplash.com/photo-1519414442781-fbd745c5b497?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  mountainSunset: "https://images.unsplash.com/photo-1611366376326-5eaf36b54355?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  hands: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // new institutional imagery
  contemplation: "https://images.unsplash.com/photo-1559296582-8bfa1d8818c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  handsSun: "https://images.unsplash.com/photo-1581812426592-49ab7fc057d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  gathering: "https://images.unsplash.com/photo-1612362426872-b42e80d6b94d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  teamTable: "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  coworkers: "https://images.unsplash.com/photo-1633113214207-1568ec4b3298?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  keynote2: "https://images.unsplash.com/photo-1594122230689-45899d9e6f69?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  auditorium: "https://images.unsplash.com/photo-1560184611-5b5749138c3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  concert: "https://images.unsplash.com/photo-1577648875929-894904f7b051?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  meditation: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  silhouetteSunset: "https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  pianoWarm: "https://images.unsplash.com/photo-1552422530-9b41dc72286b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  pianoDark: "https://images.unsplash.com/photo-1513883049090-d0b7439799bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  mistyGold: "https://images.unsplash.com/photo-1725988438041-51b11aee31b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  mountainRange: "https://images.unsplash.com/photo-1514227765494-1bbd6ba05916?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const HERO = {
  eyebrow: "",
  h1: "Advancing the Art of Human Being",
  subhead:
    "We create catalytic frameworks and experiences that deepen authenticity, connection, courage, and shared purpose—uniquely integrating music, wisdom, and immersive media to awaken our fullest humanity.",
  primaryCta: "Explore Our Work",
  secondaryCta: "Find Your Path",
  founder: "Founded and led by Emmy Award-winning artist, composer, speaker, and Resonance Catalyst Gary Malkin.",
};

export const NAV = [
  { label: "Gary's Story", to: "/story" },
  {
    label: "The Work",
    to: "/work",
    // Nav is a separate system from the Six Doorways (2608 Task List §2 — Gary explicitly
    // said the nav was not meant to become six). These three are the doorways that
    // currently have deeper pages built; they use the locked doorway names.
    children: [
      { label: "Human Capacities", to: "/work/yourself" },
      { label: "Resonance Catalyst", to: "/work/event" },
      { label: "Living Legacies", to: "/work/legacy" },
    ],
  },
  { label: "Listen", to: "/listen" },
  { label: "Gary's World", to: "/world" },
  { label: "Pricing", to: "/pricing" },
];

// Proof strip — approved metrics only (no Emmy count, no reach aggregate).
export const STATS = [
  { value: "1M+", label: "Lives touched through Graceful Passages" },
  { value: "100M+", label: "Views of projects featuring Gary's music" },
  { value: "5 YEARS", label: "Among ASCAP's most-performed television composers" },
  { value: "40+ YEARS", label: "Advancing the art of human being" },
];

export const WHY_MATTERS = {
  eyebrow: "Why This Matters",
  h: "The Future Belongs to the Deeply Human.",
  sub: "Cultivating the human capacities that matter most in an age of accelerating intelligence.",
  body: [
    "As artificial intelligence becomes more capable, our distinctly human capacities become more valuable—not less. Presence, intuition, courage, imagination, compassion, and our ability to create meaning together may become our greatest advantages.",
  ],
  pull: "AI gives us velocity. But what gives us depth, discernment, connection, and meaning?",
};

// Human Capacities visual field (1.2) — emerge within the image, not a list.
export const HUMAN_CAPACITIES = ["Presence.", "Authenticity.", "Courage.", "Connection.", "Belonging.", "Compassion.", "Awe."];

export const ECO_STORY = [
  { label: "Philosophy", sub: "The Art of Human Being", img: IMG.contemplation },
  { label: "Music", sub: "The founding medium", img: IMG.pianoWarm },
  { label: "Programs", sub: "Immersions & practices", img: IMG.keynote2 },
  { label: "Experiences", sub: "Rooms that change", img: IMG.gathering },
  { label: "Healthcare", sub: "Presence in care", img: IMG.candle },
  { label: "Technology", sub: "Vital Neuro & beyond", img: IMG.studio },
  { label: "Practitioners", sub: "Carrying the work", img: IMG.meditation },
  { label: "Partnerships", sub: "A growing ecosystem", img: IMG.coworkers },
];

// THE SIX DOORWAYS — locked visitor-facing architecture (2608 brief, §1.3).
// Each carries an `icon` per Task List §14: one coherent family from the existing
// lucide library (uniform 24px grid, 2px stroke), not bespoke commissioned art.
export const DOORWAYS = [
  {
    id: "human-capacities",
    icon: Feather, // §14: wing / transformation / becoming
    n: "01",
    title: "Human Capacities",
    intent: "I want to bring more of who I am to how I live, lead, and relate.",
    body: "Experiences and practices that cultivate authenticity, courage, connection, and coherence—helping us bring more of who we are to how we live, lead, and work together.",
    cta: "Explore Your Human Capacities",
    img: IMG.meditation,
    to: "/work/yourself",
  },
  {
    id: "resonance-catalyst",
    icon: AudioWaveform, // §14: strings / vibration / harmony / resonance
    n: "02",
    title: "Resonance Catalyst",
    intent: "I want the room to feel something they'll never forget.",
    body: "Live and immersive experiences that bring greater presence, connection, meaning, and aliveness to your events—through music, vulnerable storytelling, deep listening, and moments of our shared humanity.",
    cta: "Bring Resonance to Your Gathering",
    img: IMG.keynote2,
    to: "/work/event",
  },
  {
    id: "living-legacies",
    icon: Flame, // §14: flame / memory carried forward
    n: "03",
    title: "Living Legacies",
    intent: "I want others to experience what it feels like to know them.",
    body: "Bespoke music, voice, and media that evoke a person's presence—their story, wisdom, spirit, and way of being—creating an enduring experience of what makes them unmistakably themselves.",
    cta: "Explore Living Legacies",
    img: IMG.candle,
    to: "/work/legacy",
  },
  {
    id: "human-centered-healthcare",
    icon: HeartPulse, // §14: healing / renewal / wholeness
    n: "04",
    title: "Human-Centered Healthcare",
    intent: "I want care to feel more human—for those who give it and those who receive it.",
    body: "Experiences and resources that cultivate presence, self-regulation, and compassionate care—supporting caregivers and the people they serve through life's most human moments.",
    cta: "Explore Human-Centered Care",
    img: IMG.handsSun,
    to: "/work/healthcare",
  },
  {
    id: "presentations-performances",
    icon: MicVocal, // §14: voice / transmission / performance
    n: "05",
    title: "Presentations and Performances",
    intent: "Move me. Inspire me. Remind me what matters.",
    body: "Keynotes, conversations, and performances offering fresh perspectives and experiences that reflect what it means to be fully human—bringing ideas, story, music, and lived experience to the questions that matter now.",
    cta: "Move Your Audience",
    img: IMG.auditorium,
    to: "/work/presentations",
  },
  {
    id: "portals-of-awe",
    icon: Aperture, // §14: threshold / aperture / opening into wonder
    n: "06",
    title: "Portals of Awe",
    intent: "I want to grow, make a difference—and find my people.",
    body: "A community for courageous creators and leaders where resonance meets impact—bringing inner growth, meaningful enterprise, human connection, and contribution together.",
    cta: "Discover Portals of Awe",
    img: IMG.gathering,
    to: "/portals-of-awe",
  },
];

// The six doorways, mapped for the scroll-story engine (title + image).
export const GATEWAY_STORY = DOORWAYS.map((g) => ({ label: g.title, img: g.img }));

export const ART_OF_HUMAN_BEING = {
  eyebrow: "The Art of Human Being",
  h: "What if our greatest work is not becoming more efficient — but more fully alive?",
  body:
    "We explore that question through music, emotional intelligence, immersive experience, human coherence, storytelling, contemplation, technology, and transformational practice — a single inquiry running through many worlds.",
  pillars: [
    { label: "Music", note: "The universal language — our secret sauce." },
    { label: "Emotion", note: "Fluency with the full range of being human." },
    { label: "Presence", note: "The quality that changes every room." },
    { label: "Story", note: "How meaning is made and passed on." },
    { label: "Awe", note: "The doorway back to what matters." },
  ],
};

export const RESONANCE = {
  eyebrow: "The Method",
  quote: "",
  h: "We work by changing the quality of human presence —",
  lead: "In ourselves · In our relationships · In how we lead · In the way we gather · In the way we heal · In the stories we leave behind.",
  riHeading: "Resonance Intelligence",
  ri: "Resonance Intelligence is the capacity to meet life with greater presence, authenticity, emotional courage, discernment, and connection—and thereby change the conditions from which we live, lead, create, care, and contribute.",
  ri2: "The doorways are different. The underlying practice is the same: change the resonance, and new possibilities emerge.",
};

export const STORY_FRAMES = [
  { img: IMG.leadership, word: "lead" },
  { img: IMG.gathering, word: "gather" },
  { img: IMG.meditation, word: "heal" },
  { img: IMG.candle, word: "remember" },
];

export const FOUNDER = {
  eyebrow: "Meet Gary Malkin",
  name: "Gary Malkin",
  roles: "Artist · Speaker · Composer · Resonance Catalyst",
  body: [
    "For more than four decades, Gary Malkin has explored a defining question: how can music, emotion, wisdom, and presence awaken more of what makes us fully human?",
    "That inquiry has carried him from hospice rooms and healthcare settings to boardrooms, global stages, intimate gatherings, and the arts.",
    "This body of work grew from decades of exploration—and became an enterprise designed to carry that inquiry forward, far beyond any one person.",
  ],
  img: "/assets/video/gary-hero-portrait.jpg",
  signature: "/assets/logos/gary-signature-clean-white.webp",
};

export const ECOSYSTEM = {
  eyebrow: "The Team & Ecosystem",
  h: "No meaningful transformation happens alone.",
  body:
    "Gary Malkin Enterprises is built on the power of collaboration—bringing together artists, strategists, technologists, innovators, and wisdom keepers who share a commitment to advancing the art of human being.",
  people: [
    { name: "Gary Malkin", role: "Founder & Visionary — Artist, Composer, Wisdom Guide", bio: "Transformational catalyst and Emmy-winning composer whose work — spanning music, facilitation, and the subconscious — has moved people worldwide.", img: "/assets/video/gary-portrait-crop.jpg" },
    { name: "Adam Gaskill", role: "Strategic Advisor & Execution Architect", bio: "Adam translates the clarity you've found into a grounded, practical 90-day roadmap — systems, priorities, and momentum that move your life and leadership forward.", img: "/assets/team/adam-gaskill.webp" },
  ],
  partners: [
    { name: "Innovate For Humanity", note: "Human potential · technology · consciousness" },
    { name: "Vital Neuro", note: "Neurotechnology partner · the Integration Engine" },
    { name: "World Business Academy", note: "Legacy & stewardship collaborator" },
    { name: "Global Wisdom Foundation", note: "We Rise As One" },
  ],
  ventures: [
    { name: "Portals of Awe", note: "Coherence practice + neuro-technology", to: "/portals-of-awe" },
    { name: "Graceful Passages", note: "Music for life's thresholds", to: "/graceful-passages" },
    { name: "The Resonance Catalyst", note: "Live transformational experiences" },
    { name: "Living Legacies", note: "Alchemical Wisdom recordings" },
    { name: "Transformance", note: "Resonance for leaders" },
    { name: "Wisdom of the World", note: "The living library" },
  ],
};

export const RESONANCE_WORDS = [
  "Presence", "Authenticity", "Courage", "Connection", "Belonging",
  "Shared Purpose", "Leadership", "Interconnectedness", "Meaning", "Awe",
  "Wellbeing", "Grief Literacy", "Musical Catalysts", "Resonance", "Legacy",
];


// 1.11 — One Vision. Many Expressions. (conceptual storytelling, not clickable doorways)
export const ONE_VISION = {
  eyebrow: "One Vision. Many Expressions.",
  h: "One Vision.",
  hGold: "Many Expressions.",
  sub: "The phenomenon of resonance is the thread connecting them all.",
  blocks: [
    { label: "The Premise", title: "The Art of Human Being", line: "Being fully human is not a fixed state. It is an art we can cultivate.", img: IMG.contemplation },
    { label: "The Medium", title: "Music", line: "Music makes resonance audible, embodied, and felt.", img: IMG.pianoWarm },
    { label: "The Spark", title: "Changing the Conditions in the Room", line: "Awakening presence, authenticity, connection, and possibility.", img: IMG.keynote2 },
    { label: "The Care", title: "Humanizing Healthcare", line: "Bringing more humanity to life's most tender moments.", img: IMG.candle },
    { label: "The Collaborations", title: "Greater Than the Sum", line: "Expanding what's possible when shared vision and zones of genius converge.", img: IMG.coworkers },
    { label: "The Technology", title: "Innovation in Service of Being Human", line: "Harnessing technology to deepen presence, awareness, and connection.", img: IMG.studio },
    { label: "The Embodiment", title: "Being the Change", line: "What we embody becomes part of the world we create.", img: IMG.mountainSunrise },
  ],
};

// 1.9 — Musical Catalysts / Listening Experience: exactly three featured experiences.
// Media note: Gratitude & Do You Remember are supplied as VIDEO from Gary's artifact
// library (Dropbox handoff); until those files land locally they gracefully fall back
// to the on-site audio preview / pending state. Wisdom-of-the-World intros in the
// current media are accepted for now per client direction.
export const MUSICAL_CATALYSTS = {
  h: "Don't Just Read About It.",
  hGold: "Feel It.",
  sub: "Press play. Begin anywhere. Let the experience do the rest.",
  cta: "Enter the Full Listening Room",
  experiences: [
    {
      id: "gratitude",
      kind: "video",
      trackId: 8,
      title: "Gratitude: The Nectar of Life",
      feat: "Brother David Steindl-Rast with Gary Malkin",
      descriptor: "A filmed musical meditation on gratitude—wisdom and music woven into a single experience.",
      badge: "Featured Experience",
      mediaPending: false,
    },
    {
      id: "letting-yourself-be-loved",
      kind: "audio",
      trackId: 11,
      title: "Letting Yourself Be Loved",
      feat: "From Graceful Passages",
      descriptor: "A musical meditation on love, presence, and what matters most.",
      badge: "Listening Experience",
      mediaPending: false,
    },
    {
      id: "do-you-remember",
      kind: "video",
      trackId: null,
      title: "Do You Remember Who You Are?",
      feat: "Gary Malkin",
      descriptor: "An invitation to remember what is most alive and authentic within you.",
      badge: "Film Performance",
      mediaPending: true,
    },
  ],
};

// Closing band. CTA wording matches the Starting Point selector it opens (Task List §16).
export const FINAL = {
  eyebrow: "Begin",
  h: "There are many ways into this work.",
  hGold: "Begin with the one that speaks to where you are now.",
  primaryCta: "Find My Starting Point",
  secondaryCta: "Experience the Music",
};
